import { useCallback, useEffect, useRef, useState } from 'react';
import { DMBaseSearch, DMFacet, DMFacets, DMItem, DMUserFilters, fetchDMFacets, getDMOwnerName, searchDM, toDMItem, sortDMDocsByOrder } from '../services/DigitaltMuseum';

const NO_FACETS: DMFacets = { types: [], owners: [], subjects: [], periods: [] };

/**
 * Search DigitaltMuseum with paging and filter values (facets).
 * A new search starts when the base search, filters or sort change. Without a base search, results are cleared.
 * @param {DMBaseSearch} base Base search from the CMS. Undefined while loading, or when there is no search
 * @param {DMUserFilters} filters Filters chosen on screen
 * @param {string} sort Solr sort
 * @param {boolean} keepOrder Show a hand-picked selection (`base.order`) in the CMS order. Fetched in one request
 * @returns Results, hit count, loading and error state, facets and a function that loads the next page
 */
const useDMSearch = (base: DMBaseSearch | undefined, filters: DMUserFilters, sort?: string, keepOrder?: boolean) => {
  const [items, setItems] = useState<DMItem[]>([]);
  const [numFound, setNumFound] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [facets, setFacets] = useState<DMFacets>(NO_FACETS);
  // Responses from older searches are ignored
  const requestId = useRef<number>(0);
  // Current paging state, so loadMore never acts on stale values (it can be called from an old observer
  // callback right after a new search started)
  const paging = useRef({ count: 0, numFound: null as number | null, isLoading: false });

  // First page
  useEffect(() => {
    const id = ++requestId.current;
    paging.current = { count: 0, numFound: null, isLoading: Boolean(base) };
    setItems([]);
    setNumFound(null);
    setHasError(false);
    setIsLoading(Boolean(base));
    if (!base) { return; }

    const order = keepOrder ? base.order : undefined;
    searchDM(base, filters, { sort, rows: order?.length }).then(result => {
      if (id !== requestId.current) { return; }
      const found: any[] = result?.response?.docs || [];
      const docs = order ? sortDMDocsByOrder(found, order) : found;
      paging.current = { ...paging.current, count: docs.length, numFound: result?.response?.numFound || 0 };
      setItems(docs.map(toDMItem));
      setNumFound(paging.current.numFound);
    }).catch(() => {
      id === requestId.current && setHasError(true);
    }).finally(() => {
      if (id !== requestId.current) { return; }
      paging.current.isLoading = false;
      setIsLoading(false);
    });
  }, [base, filters, sort, keepOrder]);

  // Filter values within the base search and the other filters
  useEffect(() => {
    if (!base) { return; }
    let isSubscribed = true;
    fetchDMFacets(base, filters).then(result => {
      isSubscribed && setFacets(result);
    }).catch(() => {
      console.error("Could not fetch DM facets");
    });
    return () => { isSubscribed = false };
  }, [base, filters]);

  /**
   * Load the next page
   */
  const loadMore = useCallback(() => {
    const { count, numFound, isLoading } = paging.current;
    if (!base || isLoading || numFound === null || count >= numFound) { return; }
    const id = requestId.current;
    paging.current.isLoading = true;
    setIsLoading(true);

    searchDM(base, filters, { sort, start: count }).then(result => {
      if (id !== requestId.current) { return; }
      const docs = result?.response?.docs || [];
      paging.current.count += docs.length;
      setItems(prev => [...prev, ...docs.map(toDMItem)]);
    }).catch(() => {
      console.error("Could not load more DM results");
    }).finally(() => {
      if (id !== requestId.current) { return; }
      paging.current.isLoading = false;
      setIsLoading(false);
    });
  }, [base, filters, sort]);

  return { items, numFound, isLoading, hasError, facets, loadMore };
}

/**
 * Get the owner (museum) names for the objects in a result list and for the museum filter
 * @param {DMItem[]} items Objects in the result list
 * @param {DMFacet[]} owners Owners in the museum filter
 * @returns {object} Owner code → name
 */
export const useDMOwnerNames = (items: DMItem[], owners: DMFacet[]): Record<string, string> => {
  const [names, setNames] = useState<Record<string, string>>({});
  const requested = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Owner code → an object owned by it (used if the museum register has no name)
    const codes = new Map<string, string | undefined>();
    items.forEach(item => item.ownerCode && !codes.has(item.ownerCode) && codes.set(item.ownerCode, item.uniqueId));
    owners.length > 1 && owners.forEach(owner => !codes.has(owner.value) && codes.set(owner.value, undefined));

    codes.forEach((sampleUniqueId, code) => {
      if (requested.current.has(code)) { return; }
      requested.current.add(code);
      getDMOwnerName(code, sampleUniqueId).then(name => {
        name && setNames(prev => ({ ...prev, [code]: name }));
      });
    });
  }, [items, owners]);

  return names;
}

export default useDMSearch;
